import { isEmpty, isObject, isString, isNil, countBy, get } from 'lodash';

import {
  MIN_PASSWORD_CHAR_LIMIT,
  MAX_PASSWORD_CHAR_LIMIT,
} from 'constants/system';
import dayjs from 'services/dayjs';
import { ensureArray } from 'utils/arrays';

import { createDateTimeRange } from 'utils/dateTime';

import { PHONE_VERIFICATION_CODE_LENGTH } from 'modules/AccountSettings/components/Notifications/constants';

const defaultRequiredMessage = 'Required - please fill out this field';
const defaultURLMessage = 'Enter a valid URL';
const defaultNumberMessage = 'Enter a valid number';

export const composeValidators =
  (...validators) =>
  value =>
    validators.reduce(
      (error, validator) => error || validator(value),
      undefined,
    );

export const composeValidatorsWithContext =
  (...validators) =>
  (value, allValues, fieldState) =>
    validators.reduce(
      (error, validator) => error || validator(value, allValues, fieldState),
      undefined,
    );

export const required = (value, message) => {
  const customMessage = isString(message) ? message : defaultRequiredMessage;
  return !isNil(value) && value !== '' ? undefined : customMessage;
};

export const enumerableRequired = value =>
  !isEmpty(value) ? undefined : defaultRequiredMessage;

export const matchingPasswords = confirmPassword => password =>
  !password || (confirmPassword && password === confirmPassword)
    ? undefined
    : 'Ensure passwords match';

export const notBlankString = value =>
  typeof value === 'string' && value.trim()
    ? undefined
    : defaultRequiredMessage;

export const limitChar = (limit, errorMessage) => value =>
  value && value.length > limit
    ? errorMessage || `Too long - please reduce to ${limit} characters or fewer`
    : undefined;

export const validUrl = (value, errorMessage = defaultURLMessage) => {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url) return undefined;
  } catch {
    return errorMessage;
  }
  return errorMessage;
};

export const validateWebsite = value => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' +
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
      '((\\d{1,3}\\.){3}\\d{1,3}))' +
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
      '(\\?[;&a-z\\d%_.~+=-]*)?' +
      '(\\#[-a-z\\d_]*)?$',
    'i',
  );
  const isValidWebpage = !!pattern.test(value);
  return isValidWebpage ? undefined : defaultURLMessage;
};

export const isUrl = (value, requireProtocol) => {
  if (!value) {
    return undefined;
  }

  const urlRegex =
    // eslint-disable-next-line no-useless-escape
    /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/;

  const urlWithProtocolRegex =
    // eslint-disable-next-line no-useless-escape
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/;

  return requireProtocol
    ? urlWithProtocolRegex.test(value)
    : urlRegex.test(value);
};

export const validateUrl = (
  value,
  requireProtocol,
  errorMessage = defaultURLMessage,
) => (isUrl(value, requireProtocol) ? undefined : errorMessage);

export const isURLWithoutHTTP = value => {
  const pattern = new RegExp(
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
      '((\\d{1,3}\\.){3}\\d{1,3}))' +
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
      '(\\?[;&a-z\\d%_.~+=-]*)?' +
      '(\\#[-a-z\\d_]*)?$',
    'i',
  );
  const isValidWebpage = !!pattern.test(value);
  return isValidWebpage ? undefined : defaultURLMessage;
};

export const isNumeric = value => !/[^0-9]/.test(value);

export const httpOrHttpsUrl = value => {
  if (!value) return undefined;

  const validUrlWithoutHttpsError = isUrl(value)
    ? undefined
    : defaultURLMessage;

  if (validUrlWithoutHttpsError) {
    return validUrlWithoutHttpsError;
  }

  const validUrlError = isUrl(value, true)
    ? undefined
    : 'Must start with http:// or https://';

  if (validUrlError) {
    return validUrlError;
  }

  return undefined;
};

export const isZipCode = value => {
  // Regular expression to match valid US ZIP codes, including military address formats
  const zipCodePattern =
    /^(\d{5}(-\d{4})?|^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9])$/i;
  return zipCodePattern.test(value) ? undefined : 'Enter a valid Zip Code';
};

export const isCSV = value =>
  value.includes('.csv') ? undefined : 'File name should end in .csv';

export const mustBeInteger = value => {
  if (!value) return undefined;
  if (Number(value) === 0) return undefined;
  if (!Number(value)) return `Enter a valid number`;
  if (Number(value) < 0) return `Enter a positive number`;
  if (!Number.isInteger(+value)) return 'Enter an integer';
  return undefined;
};

export const mustBeNumber = value => {
  if (!value) return undefined;
  if (!Number.isFinite(Number(value))) return `Enter a valid number`;
  if (Number(value) === 0) return undefined;
  if (!Number(value)) return `Enter a valid number`;
  if (Number(value) < 0) return `Enter a positive number`;
  return undefined;
};

export const withinRange = (min, max) => value => {
  if (isNil(value) || !Number(value) || Number(value) === 0) return undefined;
  if (Number(value) < min || Number(value) > max)
    return `Enter a number between ${min} and ${max}`;
  return undefined;
};

export const notZero = value => {
  if (value === 0 || value === '0') return 'Enter a number greater than zero';
  if (!value) return undefined;
  return undefined;
};

export const requiredArray = value =>
  Array.isArray(value) ? undefined : defaultRequiredMessage;

export const requiredArrayLength = value =>
  Array.isArray(value) && value.length ? undefined : defaultRequiredMessage;

export const lowerThan = (limit, error) => value => {
  // Validator used incorrectly if limit is not a number, do not run validator
  if (isNil(limit) || !Number.isFinite(Number(limit)) || limit === '')
    return undefined;
  if (isNil(value) || !Number.isFinite(Number(value)) || value === '')
    return defaultNumberMessage;
  if (Number(value) >= Number(limit))
    return error || `Enter a number less than ${limit}`;
  return undefined;
};

export const higherThan = (limit, error) => value => {
  // Validator used incorrectly if limit is not a number, do not run validator
  if (isNil(limit) || !Number.isFinite(Number(limit)) || limit === '')
    return undefined;
  if (isNil(value) || !Number.isFinite(Number(value)) || value === '')
    return defaultNumberMessage;
  if (Number(value) <= Number(limit))
    return error || `Enter a number greater than ${limit}`;
  return undefined;
};

export const lowerOrEqualTo = (limit, error) => value => {
  // Validator used incorrectly if limit is not a number, do not run validator
  if (isNil(limit) || !Number.isFinite(Number(limit)) || limit === '')
    return undefined;
  if (isNil(value) || !Number.isFinite(Number(value)) || value === '')
    return defaultNumberMessage;
  if (Number(value) > Number(limit))
    return error || `Enter a number less than or equal to ${limit}`;
  return undefined;
};

export const higherOrEqualTo = (limit, error) => value => {
  // Validator used incorrectly if limit is not a number, do not run validator
  if (isNil(limit) || !Number.isFinite(Number(limit)) || limit === '')
    return undefined;
  if (isNil(value) || !Number.isFinite(Number(value)) || value === '')
    return defaultNumberMessage;
  if (Number(value) < Number(limit))
    return error || `Enter a number greater than or equal to ${limit}`;
  return undefined;
};

export const isEmail = value => {
  if (!value) {
    return undefined;
  }

  // eslint-disable-next-line no-useless-escape
  return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    value,
  );
};

export const validEmail = (value, errorMessage = 'Enter a valid email') =>
  isEmail(value) ? undefined : errorMessage;

export const validEmailForSelect = value => {
  const val = value.email || value.title || value;
  if (!val) return undefined;
  return isEmail(val) ? undefined : 'Enter a valid email';
};

export const dateLowerThan = limit => value => {
  if (!limit) {
    return undefined;
  }
  if (limit && !dayjs(limit).isValid()) {
    return undefined;
  }
  if (dayjs(value).isSame(dayjs(limit), 'day'))
    return `Enter a date before ${dayjs(limit).format('MM/DD/YYYY')}`;
  return value && dayjs(value).isAfter(limit)
    ? `Enter a date before ${dayjs(limit).format('MM/DD/YYYY')}`
    : undefined;
};

export const dateHigherThan = limit => value => {
  if (!limit) {
    return undefined;
  }
  if (limit && !dayjs(limit).isValid()) {
    return undefined;
  }
  if (dayjs(value).isSame(dayjs(limit), 'day'))
    return `Enter a date after ${dayjs(limit).format('MM/DD/YYYY')}`;
  return value && dayjs(value).isBefore(limit)
    ? `Enter a date after ${dayjs(limit).format('MM/DD/YYYY')}`
    : undefined;
};

export const dateNotInPast =
  (message = `Date can't be in the past`) =>
  value => {
    if (!value) {
      return undefined;
    }
    if (value && !dayjs(value).isValid()) {
      return undefined;
    }

    return dayjs(value).isBefore(dayjs().subtract(1, 'day'))
      ? message
      : undefined;
  };

export const timeLowerThan = limit => value => {
  if (!limit) {
    return undefined;
  }
  if (limit && !dayjs(limit).isValid()) {
    return undefined;
  }
  if (
    value &&
    limit &&
    dayjs(value).format('hh:mma') === dayjs(limit).format('hh:mma')
  )
    return `Enter a time before ${dayjs(limit).format('hh:mma')}`;
  return value && limit && dayjs(value).isBefore(dayjs(limit))
    ? undefined
    : `Enter a time before ${dayjs(limit).format('hh:mma')}`;
};

export const timeHigherThan = limit => value => {
  if (!limit) {
    return undefined;
  }
  if (limit && !dayjs(limit).isValid()) {
    return undefined;
  }
  if (
    value &&
    limit &&
    dayjs(value).format('hh:mma') === dayjs(limit).format('hh:mma')
  )
    return `Enter a time after ${dayjs(limit).format('hh:mma')}`;
  return value && limit && dayjs(value).isAfter(dayjs(limit))
    ? undefined
    : `Can't be after ${dayjs(limit).format('hh:mma')}`;
};

export const dateIsValid = value => {
  if (!value) return 'Required';
  try {
    if (!dayjs(value).isValid()) return 'Enter a valid date';
  } catch {
    return 'Enter a valid date';
  }
  return undefined;
};

export const validateDateWithFormat = (value, format, isStrict = 'true') => {
  try {
    if (!dayjs(value, format, isStrict).isValid()) return 'Enter a valid date';
  } catch {
    return 'Enter a valid date';
  }
  return undefined;
};

export const dateIsValidOrEmpty = value => value && dateIsValid(value);

export const timeIsValid = value => {
  try {
    if (!dayjs(value).isValid()) return 'Enter a valid time';
  } catch {
    return 'Enter a valid time';
  }
  return undefined;
};

export const validatePassword = value => {
  if (!value) return 'Enter a password';

  if (value && value.length < MIN_PASSWORD_CHAR_LIMIT) {
    return `Create a password that is at least ${MIN_PASSWORD_CHAR_LIMIT} characters long`;
  }

  if (value && value.length > MAX_PASSWORD_CHAR_LIMIT) {
    return `Create a password that is not longer than ${MAX_PASSWORD_CHAR_LIMIT} characters`;
  }

  const hasNoNumber = !/[0-9]/.test(value);
  const hasNoUpperCaseLetter = !/[A-Z]/.test(value);
  const hasNoLowerCaseLetter = !/[a-z]/.test(value);

  if (hasNoNumber || hasNoUpperCaseLetter || hasNoLowerCaseLetter)
    return `Your password must be ${MIN_PASSWORD_CHAR_LIMIT}–${MAX_PASSWORD_CHAR_LIMIT} characters long and include at least one lowercase letter, one uppercase letter, and one number.`;

  return undefined;
};

export const ageHigherThan = limit => value => {
  if (!value) return undefined;
  if (!dayjs(value).isValid()) return 'Enter a valid date';
  const age = dayjs().diff(value, 'years');
  if (age < limit) return `You must be older than ${limit} years`;
  return undefined;
};

export const limitWords = (limit, errorMessage) => value => {
  if (!value) return undefined;
  const wordsLength = value?.split(' ')?.length;
  if (limit && wordsLength > limit)
    return errorMessage || `Enter response fewer than ${limit} words`;
  return undefined;
};

export const rangeWords = (min, max, minErrorText, maxErrorText) => value => {
  if (!value) return undefined;
  const wordsLength = value?.split(' ')?.length;
  if (min && wordsLength < min)
    return minErrorText || `Enter response longer than ${min} words`;
  if (max && wordsLength > max)
    return maxErrorText || `Enter response fewer than ${max} words`;
  return undefined;
};

export const rangeChars = (min, max, errorMessage) => value => {
  if (!value?.trim()) return undefined;
  const wordsLength = value?.trim()?.length;
  if (min && wordsLength < min)
    return errorMessage || `Enter response longer than ${min} characters`;
  if (max && wordsLength > max)
    return errorMessage || `Enter response fewer than ${max} characters`;
  return undefined;
};

export const validatePhoneNumber = value =>
  value && /^\d+-*\d*$/gim.test(value) && value.length === 10;

export const validPhone = (
  value,
  errorMessage = 'Enter a valid phone number',
) => (validatePhoneNumber(value) ? undefined : errorMessage);

export const validOptionalPhone = (
  value,
  errorMessage = 'Enter a valid phone number',
) => {
  if (!value) return undefined;
  return !validatePhoneNumber(value) && errorMessage;
};

export const validatePhoneVerificationCode = value =>
  value &&
  /^\d+-*\d*$/gim.test(value) &&
  value.length === PHONE_VERIFICATION_CODE_LENGTH;

export const durationRequired = value => {
  if (!value || value === '00:00' || value === '00:00:00') {
    return defaultRequiredMessage;
  }
  return undefined;
};

export const onlyAlphanumericDashAndUnderscore = value => {
  if (!/^[a-zA-Z0-9_-]*$/g.test(value)) {
    return 'Ensure following characters only: A-Z a-z 0-9 - _';
  }
  return undefined;
};

export const metaError = meta =>
  (meta.error || meta.submitError) && meta.touched;
export const metaHelperText = meta =>
  (meta.error || meta.submitError) && meta.touched
    ? meta.error || meta.submitError
    : '';

export const requiredValueIsObject = value =>
  isObject(value) ? undefined : defaultRequiredMessage;

export const atLeastOneRequired = value =>
  Array.isArray(value) && value.length > 0
    ? undefined
    : 'Select at least one option';

/*
 * This validator checks if any duplicates exist in a field array
 * `path` must point to an array of objects in the form object
 */
export const validateDuplicatesInFieldArray = ({
  value,
  fieldName,
  form,
  path,
}) => {
  const allArrayValues = get(form.getState().values, path);
  const allFields = ensureArray(allArrayValues).map(
    termObj => termObj[fieldName],
  );
  const counts = countBy(allFields);
  if (counts[value] > 1) {
    return 'Remove duplicated values';
  }
  return null;
};

export const validatePasswordMatch = (value, allValues = {}) => {
  if (!value) {
    return undefined;
  }

  if (value === allValues.password) {
    return undefined;
  }

  return 'Please make sure your passwords match.';
};

export const validTimeRange = startTime => endTime => {
  const dummyDate = '2000-01-01';
  const { startDateTime, endDateTime } = createDateTimeRange({
    date: dummyDate,
    startTime,
    endTime,
  });

  if (
    endDateTime.isBefore(startDateTime) ||
    endDateTime.isSame(startDateTime)
  ) {
    return 'End time must be after end time.';
  }

  return undefined;
};
